/**
 * CRM mini: hook per timeline attività commerciali su un progetto.
 *
 * Tipi: call / email / meeting / site_visit / note / reminder.
 * linked_estimate_id opzionale: collega attività a revisione preventivo specifica.
 * follow_up_at opzionale: usato dal widget dashboard "follow-up imminenti".
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';

export type ProjectActivityType =
  | 'call'
  | 'email'
  | 'meeting'
  | 'site_visit'
  | 'note'
  | 'reminder';

export interface ProjectActivity {
  id: string;
  project_id: string;
  organization_id: string;
  type: ProjectActivityType;
  subject: string;
  body: string | null;
  contact_at: string | null;
  /** Quando il contatto programmato è stato fatto. NULL = ancora da fare. */
  contact_done_at: string | null;
  follow_up_at: string | null;
  /** Quando il follow-up è stato completato. NULL = ancora da fare. */
  completed_at: string | null;
  linked_estimate_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Embed opzionale per UI: preventivo collegato con nome+versione. */
  estimates?: { id: string; name: string; version: number } | null;
}

export interface CreateProjectActivityInput {
  project_id: string;
  type: ProjectActivityType;
  subject: string;
  body?: string | null;
  contact_at?: string | null;
  contact_done_at?: string | null;
  follow_up_at?: string | null;
  linked_estimate_id?: string | null;
}

/** Patch parziale per update: tutti i campi sono opzionali. */
export interface UpdateProjectActivityInput {
  id: string;
  type?: ProjectActivityType;
  subject?: string;
  body?: string | null;
  contact_at?: string | null;
  contact_done_at?: string | null;
  follow_up_at?: string | null;
  linked_estimate_id?: string | null;
}

const ACTIVITIES_KEY = (projectId: string) => ['project-activities', projectId];

export function useProjectActivities(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();

  const { data: activities = [], isLoading } = useQuery<ProjectActivity[]>({
    queryKey: ACTIVITIES_KEY(projectId ?? ''),
    enabled: !!projectId,
    queryFn: async () => {
      if (!projectId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('project_activities')
        .select('*, estimates ( id, name, version )')
        .eq('project_id', projectId)
        // Ordina per data evento: prima i futuri (reminder), poi storico recente.
        // contact_at NULL (reminder puri) → uso follow_up_at.
        .order('contact_at', { ascending: false, nullsFirst: true });
      if (error) {
        console.error('[useProjectActivities] error:', error);
        throw error;
      }
      return (data ?? []) as ProjectActivity[];
    },
  });

  const createActivity = useMutation({
    mutationFn: async (input: CreateProjectActivityInput): Promise<ProjectActivity> => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione attiva');
      const now = new Date().toISOString();
      // Default contact_at = now per tutti tranne 'reminder' (può essere solo futuro)
      const contactAt = input.contact_at !== undefined
        ? input.contact_at
        : (input.type === 'reminder' ? null : now);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('project_activities')
        .insert({
          project_id: input.project_id,
          organization_id: currentOrganizationId,
          type: input.type,
          subject: input.subject.trim(),
          body: input.body?.trim() || null,
          contact_at: contactAt,
          contact_done_at: input.contact_done_at ?? null,
          follow_up_at: input.follow_up_at || null,
          linked_estimate_id: input.linked_estimate_id || null,
          created_by: user?.id ?? null,
        })
        .select('*, estimates ( id, name, version )')
        .single();
      if (error) throw error;
      return data as ProjectActivity;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ACTIVITIES_KEY(vars.project_id) });
      // Invalida anche il widget follow-up imminenti (futuro)
      queryClient.invalidateQueries({ queryKey: ['follow-up-imminent'] });
      toast.success('Attività registrata');
    },
    onError: (error: Error) => {
      console.error('[createActivity] error:', error);
      toast.error(`Errore: ${error.message}`);
    },
  });

  const updateActivity = useMutation({
    mutationFn: async (input: UpdateProjectActivityInput): Promise<ProjectActivity> => {
      const patch: Record<string, unknown> = {};
      if (input.type !== undefined)               patch.type = input.type;
      if (input.subject !== undefined)            patch.subject = input.subject.trim();
      if (input.body !== undefined)               patch.body = input.body?.trim() || null;
      if (input.contact_at !== undefined)         patch.contact_at = input.contact_at;
      if (input.contact_done_at !== undefined)    patch.contact_done_at = input.contact_done_at;
      if (input.follow_up_at !== undefined)       patch.follow_up_at = input.follow_up_at;
      if (input.linked_estimate_id !== undefined) patch.linked_estimate_id = input.linked_estimate_id || null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('project_activities')
        .update(patch)
        .eq('id', input.id)
        .select('*, estimates ( id, name, version )')
        .single();
      if (error) throw error;
      return data as ProjectActivity;
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ACTIVITIES_KEY(projectId) });
      }
      queryClient.invalidateQueries({ queryKey: ['follow-up-imminent'] });
      toast.success('Attività aggiornata');
    },
    onError: (error: Error) => {
      console.error('[updateActivity] error:', error);
      toast.error(`Errore: ${error.message}`);
    },
  });

  const deleteActivity = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('project_activities')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ACTIVITIES_KEY(projectId) });
      }
      queryClient.invalidateQueries({ queryKey: ['follow-up-imminent'] });
      toast.success('Attività eliminata');
    },
    onError: (error: Error) => {
      console.error('[deleteActivity] error:', error);
      toast.error(`Errore: ${error.message}`);
    },
  });

  /**
   * Applica un patch ottimistico su tutte le query "all-activities" e "project-activities".
   * Le query con onlyPending=true (queryKey[2]) rimuovono l'item se diventa completato,
   * o lo lasciano fuori se viene riaperto (il refetch successivo lo riaggiungerà).
   * Le altre query lo aggiornano in-place.
   */
  const applyOptimisticPatch = (
    id: string,
    patch: Partial<Pick<ProjectActivity, 'completed_at' | 'contact_done_at'>>
  ): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isAnyPending = (a: any): boolean => {
      const contactPending = !!a.contact_at && !a.contact_done_at;
      const followUpPending = !!a.follow_up_at && !a.completed_at;
      return contactPending || followUpPending;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allEntries = queryClient.getQueriesData<any[]>({ queryKey: ['all-activities'] });
    for (const [key, data] of allEntries) {
      if (!Array.isArray(data)) continue;
      const onlyPending = key[2] === true;
      // Applica il patch in-place, poi (per le query "solo da fare") rimuovi
      // l'item se nessuna delle due task è più pendente.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const patched = data.map((a) => (a.id === id ? { ...a, ...patch } : a));
      const next = onlyPending ? patched.filter(isAnyPending) : patched;
      queryClient.setQueryData(key, next);
    }
    if (projectId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData<any[]>(ACTIVITIES_KEY(projectId), (old) =>
        old?.map((a) => (a.id === id ? { ...a, ...patch } : a))
      );
    }
  };

  /**
   * Marca un follow-up come "fatto" — setta completed_at=now().
   * Preserva follow_up_at (data programmata) per permettere il toggle inverso.
   */
  const completeReminder = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('project_activities')
        .update({ completed_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['all-activities'] });
      applyOptimisticPatch(id, { completed_at: new Date().toISOString() });
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ACTIVITIES_KEY(projectId) });
      }
      queryClient.invalidateQueries({ queryKey: ['all-activities'] });
      queryClient.invalidateQueries({ queryKey: ['sidebar-counts'] });
      toast.success('Follow-up completato');
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['all-activities'] });
      toast.error('Impossibile completare il follow-up');
    },
  });

  /** Marca il contatto come "fatto" — setta contact_done_at=now(). */
  const completeContact = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('project_activities')
        .update({ contact_done_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['all-activities'] });
      applyOptimisticPatch(id, { contact_done_at: new Date().toISOString() });
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ACTIVITIES_KEY(projectId) });
      }
      queryClient.invalidateQueries({ queryKey: ['all-activities'] });
      queryClient.invalidateQueries({ queryKey: ['sidebar-counts'] });
      toast.success('Contatto completato');
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['all-activities'] });
      toast.error('Impossibile completare il contatto');
    },
  });

  /** Riapre il contatto — azzera contact_done_at. */
  const reopenContact = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('project_activities')
        .update({ contact_done_at: null })
        .eq('id', id);
      if (error) throw error;
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['all-activities'] });
      applyOptimisticPatch(id, { contact_done_at: null });
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ACTIVITIES_KEY(projectId) });
      }
      queryClient.invalidateQueries({ queryKey: ['all-activities'] });
      queryClient.invalidateQueries({ queryKey: ['sidebar-counts'] });
      toast.success('Contatto riaperto');
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['all-activities'] });
      toast.error('Impossibile riaprire il contatto');
    },
  });

  /** Riapre un follow-up completato — azzera completed_at. */
  const reopenReminder = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('project_activities')
        .update({ completed_at: null })
        .eq('id', id);
      if (error) throw error;
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['all-activities'] });
      applyOptimisticPatch(id, { completed_at: null });
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ACTIVITIES_KEY(projectId) });
      }
      queryClient.invalidateQueries({ queryKey: ['all-activities'] });
      queryClient.invalidateQueries({ queryKey: ['sidebar-counts'] });
      toast.success('Follow-up riaperto');
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['all-activities'] });
      toast.error('Impossibile riaprire il follow-up');
    },
  });

  return {
    activities,
    isLoading,
    createActivity: createActivity.mutate,
    isCreating: createActivity.isPending,
    updateActivity: updateActivity.mutate,
    isUpdating: updateActivity.isPending,
    deleteActivity: deleteActivity.mutate,
    isDeleting: deleteActivity.isPending,
    completeReminder: completeReminder.mutate,
    isCompleting: completeReminder.isPending,
    reopenReminder: reopenReminder.mutate,
    isReopening: reopenReminder.isPending,
    completeContact: completeContact.mutate,
    isCompletingContact: completeContact.isPending,
    reopenContact: reopenContact.mutate,
    isReopeningContact: reopenContact.isPending,
  };
}
